import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomNav from '@/components/feature/BottomNav';
import { mockHabits, mockHeatmap, mockGrowthRecords } from '@/mocks/userData';

type TabType = 'all' | 'active' | 'done';

// 蓝紫色系热力图四级色
const heatLevels = [
  'rgba(181,198,224,0.12)',
  'rgba(181,198,224,0.35)',
  'rgba(168,184,216,0.6)',
  'rgba(140,160,200,0.88)',
];

export default function HabitPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabType>('all');
  const [habits, setHabits] = useState(mockHabits);
  const [showFab, setShowFab] = useState(false);

  const presetHabits = [
    { name: '早起一杯水', icon: 'ri-cup-line' },
    { name: '冥想5分钟', icon: 'ri-mental-health-line' },
    { name: '感恩日记', icon: 'ri-quill-pen-line' },
    { name: '深呼吸3次', icon: 'ri-lungs-line' },
    { name: '睡前拉伸', icon: 'ri-walk-line' },
  ];

  const handleCheck = (id: string) => {
    setHabits(prev => prev.map(h => {
      if (h.id !== id) return h;
      const checked = !h.checkedToday;
      return {
        ...h,
        checkedToday: checked,
        streak: checked ? h.streak + 1 : Math.max(0, h.streak - 1),
        progress: { ...h.progress, done: checked ? Math.min(h.progress.done + 1, h.progress.total) : Math.max(0, h.progress.done - 1) },
      };
    }));
  };

  const filtered = habits.filter(h => {
    if (tab === 'active') return !h.checkedToday;
    if (tab === 'done') return h.checkedToday;
    return true;
  });

  const todayPhotos = mockGrowthRecords.filter(r => r.date === '2026-04-19').reduce((acc, r) => acc + r.photos.length, 0);

  return (
    <div className="h-full flex flex-col relative" style={{ background: '#F5F7FA', fontFamily: '\'Noto Sans SC\', sans-serif' }}>
      <div className="flex-1 overflow-y-auto relative" style={{ scrollbarWidth: 'none' }}>
        <div className="absolute top-0 left-0 right-0 h-52 pointer-events-none" style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(181,198,224,0.2) 0%, transparent 80%)' }} />

        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-12 pb-4">
          <div>
            <div className="flex items-baseline gap-1">
              <span className="font-bold tabular-nums" style={{ fontSize: 32, color: '#A8B8D8' }}>12</span>
              <span className="text-sm" style={{ color: '#2D2B3D' }}>天</span>
            </div>
            <p className="text-xs" style={{ color: '#8E8E93' }}>连续打卡</p>
          </div>
          {/* 蓝紫色系迷你热力图 */}
          <div className="flex flex-col gap-0.5">
            {[0, 1, 2, 3, 4].map(row => (
              <div key={row} className="flex gap-0.5">
                {mockHeatmap.slice(row * 6, row * 6 + 6).map((d, i) => (
                  <div key={i} className="w-3 h-3 rounded-sm" style={{ background: heatLevels[d.level] }} />
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Tab */}
        <div className="flex px-4 gap-5 mb-3">
          {([['all', '全部'], ['active', '进行中'], ['done', '已完成']] as [TabType, string][]).map(([t, label]) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className="relative pb-1.5 text-sm cursor-pointer whitespace-nowrap transition-colors"
              style={{ color: tab === t ? '#A8B8D8' : '#B0B0B0', fontWeight: tab === t ? 600 : 400 }}
            >
              {label}
              {tab === t && <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full" style={{ background: 'linear-gradient(90deg,#B5C6E0,#C5B9D8)' }} />}
            </button>
          ))}
        </div>

        {/* 成长记录卡片入口 */}
        <div className="px-4 mb-3">
          <button
            onClick={() => navigate('/growth')}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl cursor-pointer active:scale-95 transition-transform"
            style={{
              background: 'rgba(255,255,255,0.75)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(181,198,224,0.22)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.85)',
            }}
          >
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg,#B5C6E0,#C5B9D8)' }}>
              <i className="ri-camera-line text-white text-base" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-semibold" style={{ color: '#A8B8D8' }}>成长记录</p>
              <p className="text-xs mt-0.5" style={{ color: '#C5B9D8' }}>记录每日进步瞬间</p>
            </div>
            <div className="flex items-center gap-1.5">
              {todayPhotos > 0 && (
                <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(181,198,224,0.18)', color: '#A8B8D8' }}>
                  {todayPhotos}张
                </span>
              )}
              <i className="ri-arrow-right-s-line" style={{ color: '#C5B9D8' }} />
            </div>
          </button>
        </div>

        {/* Habit list — 底部加 pb-24 防止被底部导航遮挡 */}
        <div className="px-4 flex flex-col gap-3 pb-24">
          {filtered.map(habit => (
            <div
              key={habit.id}
              className="rounded-2xl p-4 flex items-center gap-3 relative overflow-hidden"
              style={{
                background: 'rgba(255,255,255,0.78)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                border: '1px solid rgba(181,198,224,0.2)',
              }}
            >
              {habit.checkedToday && (
                <div className="absolute inset-0 pointer-events-none rounded-2xl" style={{ background: 'rgba(181,198,224,0.06)' }} />
              )}
              <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(181,198,224,0.18)' }}>
                <i className={`${habit.icon} text-lg`} style={{ color: '#A8B8D8' }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm mb-1" style={{ color: '#2D2B3D' }}>{habit.name}</p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(181,198,224,0.18)' }}>
                    <div className="h-full rounded-full" style={{ width: `${(habit.progress.done / habit.progress.total) * 100}%`, background: 'linear-gradient(90deg,#B5C6E0,#C5B9D8)' }} />
                  </div>
                  <span className="text-xs whitespace-nowrap" style={{ color: '#8E8E93' }}>{habit.progress.done}/{habit.progress.total}天</span>
                </div>
                <span className="inline-block mt-1.5 px-2 py-0.5 rounded-full text-xs" style={{ background: 'rgba(181,198,224,0.15)', color: '#A8B8D8' }}>
                  连续{habit.streak}天
                </span>
              </div>
              <button
                onClick={() => handleCheck(habit.id)}
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 cursor-pointer active:scale-90 transition-all"
                style={{
                  background: habit.checkedToday ? 'linear-gradient(135deg,#B5C6E0,#C5B9D8)' : 'transparent',
                  border: habit.checkedToday ? 'none' : '2px solid #C5B9D8',
                }}
              >
                {habit.checkedToday && <i className="ri-check-line text-white text-base" />}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* FAB — 位置在底部导航上方 */}
      <button
        onClick={() => setShowFab(true)}
        className="absolute right-5 z-40 w-14 h-14 rounded-full flex items-center justify-center cursor-pointer active:scale-90 transition-transform"
        style={{ bottom: 76, background: 'linear-gradient(135deg,#B5C6E0,#C5B9D8)', boxShadow: '0 8px 24px rgba(181,198,224,0.5)' }}
      >
        <i className="ri-add-line text-white text-2xl" />
      </button>

      {showFab && (
        <div className="absolute inset-0 z-50 flex flex-col justify-end" onClick={() => setShowFab(false)}>
          <div className="absolute inset-0 bg-black/20" />
          <div
            className="relative rounded-t-3xl p-6"
            style={{ background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(16px)' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="w-10 h-1 rounded-full mx-auto mb-4" style={{ background: 'rgba(181,198,224,0.4)' }} />
            <p className="font-semibold text-base mb-4" style={{ color: '#2D2B3D' }}>推荐习惯</p>
            <div className="flex flex-col gap-3">
              {presetHabits.map(p => (
                <button
                  key={p.name}
                  onClick={() => setShowFab(false)}
                  className="flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-transform active:scale-95"
                  style={{ background: 'rgba(181,198,224,0.1)', border: '1px solid rgba(181,198,224,0.22)' }}
                >
                  <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: 'rgba(181,198,224,0.22)' }}>
                    <i className={`${p.icon} text-base`} style={{ color: '#A8B8D8' }} />
                  </div>
                  <span className="text-sm font-medium" style={{ color: '#2D2B3D' }}>{p.name}</span>
                  <i className="ri-add-circle-line ml-auto text-lg" style={{ color: '#C5B9D8' }} />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
